import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactComponent as Logo } from '../../assets/svg/logo-new.svg'
import { ReactComponent as LogoIcon } from '../../assets/svg/logoicon.svg'
import { RiMenuUnfold2Line } from 'react-icons/ri'
import IconButton from '../../elements/iconButton'
import { FiEdit2, FiMessageSquare, FiMoreHorizontal, FiSearch, FiShare2, FiTrash2, FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { useTestOpsGenieService } from './services/testopsGenieService'
import { useChatToExperimentApi } from '../../store/api/hooks/chatToExperimentApiHook'
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'
import { IoMdAdd } from 'react-icons/io'
import { trackEvent } from '../../_common/functions'

interface Props {
    open: boolean
    token: string | null
    close: () => void
    fullscreen: boolean
    chatModel?: 'experiment' | 'insight'
    onNewExperimentChat?: () => void
    onLoadExperimentSession?: (sessionId: string) => void
    activeSessionId?: string | null
    setConversation: (id: any) => void
    leftPannelCollapsed: boolean
    setLeftPannelCollapsed: ((collapsed: boolean) => void) | null
    projectKey: string
}

export interface ExperimentSessionItem {
    session_id: string
    name: string
    created_at: string
}

export interface ChatItem {
    conversation_id: string
    name: string
    created_at: string // ISO date
    updated_at: string // ISO date
    user_object_id: string
}

type ChatMenuProps = Readonly<{
    onShare: () => void
    onRename: () => void
    onDelete: () => void
}>

function ChatContextMenu({ onShare, onRename, onDelete }: ChatMenuProps) {
    const { t } = useTranslation()
    return (
        <div
            className="
        absolute right-2 top-10 z-50
        w-40
        rounded-xl
        bg-white
        shadow-lg
        border border-gray-100
        py-1
      "
        >
            <MenuItem icon={<FiShare2 />} label={t('Share')} onClick={onShare} />
            <MenuItem icon={<FiEdit2 />} label={t('Rename')} onClick={onRename} />
            <div className="my-1 h-px bg-gray-100" />
            <MenuItem icon={<FiTrash2 className="text-red-500" />} label={t('Delete')} className="text-red-500 hover:bg-red-50" onClick={onDelete} />
        </div>
    )
}

function MenuItem({
    icon,
    label,
    onClick,
    className = '',
}: Readonly<{
    icon: React.ReactNode
    label: string
    onClick: () => void
    className?: string
}>) {
    return (
        <button type="button" onClick={onClick} className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ${className}`}>
            {icon}
            {label}
        </button>
    )
}

const getDateGroupLabel = (dateStr: string): string => {
    const now = new Date()
    const today = now.toDateString()
    const yesterday = new Date(Date.now() - 86400000).toDateString()
    const lastWeekStart = new Date(Date.now() - 7 * 86400000)
    const d = new Date(dateStr)
    const dStr = d.toDateString()
    if (dStr === today) return 'Today'
    if (dStr === yesterday) return 'Yesterday'
    if (d >= lastWeekStart) return 'Last Week'
    return 'Older'
}

const DATE_GROUP_ORDER = ['Today', 'Yesterday', 'Last Week', 'Older']

export const groupChatsByDate = (chats: ChatItem[]) => {
    const grouped = chats.reduce<Record<string, ChatItem[]>>((acc, chat) => {
        const key = getDateGroupLabel(chat.updated_at)
        if (!acc[key]) acc[key] = []
        acc[key].push(chat)
        return acc
    }, {})
    const result: Record<string, ChatItem[]> = {}
    for (const key of DATE_GROUP_ORDER) {
        if (grouped[key]) result[key] = grouped[key]
    }
    return result
}

export const groupSessionsByDate = (sessions: ExperimentSessionItem[]) => {
    const grouped = sessions.reduce<Record<string, ExperimentSessionItem[]>>((acc, session) => {
        const key = getDateGroupLabel(session.created_at)
        if (!acc[key]) acc[key] = []
        acc[key].push(session)
        return acc
    }, {})
    const result: Record<string, ExperimentSessionItem[]> = {}
    for (const key of DATE_GROUP_ORDER) {
        if (grouped[key]) result[key] = grouped[key]
    }
    return result
}

const CollapsibleSection: React.FC<{ title: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, defaultOpen = true, children }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen)
    return (
        <div className="rounded-xl border border-gray-200 border-b-[#F5E003] border-b-2 bg-white flex-shrink-0">
            <button
                type="button"
                className="flex items-center justify-between w-full px-4 py-2.5 bg-white hover:bg-gray-50 transition rounded-xl"
                onClick={() => {
                    trackEvent('ai_history_date_section_toggled', { section: title, expanded: !isOpen })
                    setIsOpen(!isOpen)
                }}
            >
                <span className="text-xs font-bold text-gray-800 uppercase">{title}</span>
                {isOpen ? <FiChevronDown size={14} className="text-gray-500" /> : <FiChevronRight size={14} className="text-gray-500" />}
            </button>
            {isOpen && <div className="pb-2">{children}</div>}
        </div>
    )
}

const ChatRow = ({ chat, setConversation }: { chat: ChatItem; setConversation: (id: any) => void }) => {
    const loadConversation = () => {
        trackEvent('ai_history_insight_chat_selected', { conversation_id: chat.conversation_id })
        setConversation(chat.conversation_id)
    }

    return (
        <li className="relative group">
            <button type="button" className="flex items-center justify-between w-full px-4 py-2 cursor-pointer hover:bg-gray-50 transition" onClick={loadConversation}>
                <div className="flex items-center gap-2 truncate">
                    <FiMessageSquare className="text-gray-400 flex-shrink-0" size={14} />
                    <span className="truncate text-xs font-medium text-gray-700">{chat.name}</span>
                </div>
                <span className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 flex-shrink-0">
                    <FiMoreHorizontal size={14} />
                </span>
            </button>
        </li>
    )
}

const TestOpsGenieHistory: React.FC<Props> = ({ open, token, close, fullscreen, setConversation, chatModel, onNewExperimentChat, onLoadExperimentSession, activeSessionId, leftPannelCollapsed, setLeftPannelCollapsed, projectKey }) => {
    const { t } = useTranslation()
    const [history, setHistory] = useState<any[] | null>(null)
    const [grouped, setGrouped] = useState<any>({})
    const [experimentSessions, setExperimentSessions] = useState<ExperimentSessionItem[]>([])
    const { getConversationList } = useTestOpsGenieService()
    const chatToExperimentAPI = useChatToExperimentApi()

    const _getConversationList = async () => {
        const response: any = await getConversationList({
            access_token: token as string,
            page: 1,
            page_size: 30,
            project_key: projectKey,
        })
        setHistory(response)
        trackEvent('ai_history_insight_chats_loaded', { count: Array.isArray(response) ? response.length : 0 })
    }

    const loadExperimentSessions = () => {
        chatToExperimentAPI.listSessions(
            {},
            (resp: any) => {
                const sessions = resp.data?.result || []
                console.log('Experiment sessions loaded:', sessions)
                trackEvent('ai_history_sessions_loaded', { count: sessions.length })
                setExperimentSessions(sessions.map((s: any) => ({ session_id: s.session_id, created_at: s.created_at, name: s.name })))
            },
            () => {
                trackEvent('ai_history_sessions_load_failed', {})
                setExperimentSessions([])
            },
        )
    }

    const getSessionLabel = (session: ExperimentSessionItem): string => {
        if (session.created_at) {
            return session.name || session.session_id
        }
        return 'New conversation'
    }

    useEffect(() => {
        if (open || fullscreen) {
            if (chatModel === 'experiment') {
                loadExperimentSessions()
            } else {
                _getConversationList().catch((error) => {
                    console.error('Failed to load conversation history:', error)
                })
            }
        }
    }, [open, fullscreen, chatModel])

    useEffect(() => {
        if (history !== null) {
            setGrouped(groupChatsByDate(history as any))
        }
    }, [history])

    return (
        <div className={`w-full h-full flex flex-col gap-4 `}>
            <div className="flex items-center justify-between">
                {leftPannelCollapsed && fullscreen ? <LogoIcon width={'2rem'} height={'2rem'} /> : <Logo width={'8rem'} height={'2.4rem'} />}
                {setLeftPannelCollapsed && (
                    <>
                        {fullscreen ? (
                            <button
                                type="button"
                                className={`${leftPannelCollapsed ? 'hidden' : ''} w-7 h-7 flex items-center justify-center text-xl rounded-full bg-primary`}
                                onClick={() => {
                                    trackEvent('ai_history_panel_collapsed', {})
                                    setLeftPannelCollapsed?.(!leftPannelCollapsed)
                                }}
                            >
                                <MdKeyboardArrowLeft />
                            </button>
                        ) : (
                            <IconButton
                                icon={<RiMenuUnfold2Line className="text-[1rem]" />}
                                onClick={() => {
                                    trackEvent('ai_history_drawer_closed', { source: 'history_panel' })
                                    close()
                                }}
                            />
                        )}
                    </>
                )}
            </div>

            {fullscreen && leftPannelCollapsed ? (
                <>
                    <button
                        type="button"
                        className={`w-8 h-8 flex items-center justify-center text-xl rounded-full bg-white border border-slate-300`}
                        onClick={() => {
                            trackEvent('ai_history_panel_expanded', {})
                            setLeftPannelCollapsed?.(!leftPannelCollapsed)
                        }}
                    >
                        <FiSearch />
                    </button>
                    <button
                        type="button"
                        className={`w-8 h-8 flex items-center justify-center text-xl rounded-full bg-primary`}
                        onClick={() => {
                            trackEvent('ai_history_new_chat_clicked', { model: chatModel === 'experiment' ? 'experiment' : 'insight' })
                            if (chatModel === 'experiment' && onNewExperimentChat) {
                                onNewExperimentChat()
                            } else {
                                setConversation(null)
                            }
                        }}
                    >
                        <IoMdAdd />
                    </button>

                    <button
                        type="button"
                        className={`w-8 h-8 flex items-center justify-center text-xl rounded-full bg-white border border-slate-300`}
                        onClick={() => {
                            trackEvent('ai_history_panel_expanded', {})
                            setLeftPannelCollapsed?.(!leftPannelCollapsed)
                        }}
                    >
                        <MdKeyboardArrowRight />
                    </button>
                </>
            ) : (
                <>
                    <div className="w-full ">
                        <div className="relative">
                            {/* Search Icon */}
                            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />

                            {/* Input */}
                            <input
                                type="text"
                                placeholder={t('Search...')}
                                className="
            w-full rounded-full
            border border-[#F3F3F3]
            py-2 pl-10 pr-4 text-sm
            focus:border-blue-500
            placeholder:text-[#818181]
            text-slate-800
            focus:outline-none
            focus:ring-1 focus:ring-blue-500
          "
                            />
                        </div>
                    </div>

                    <button
                        type="button"
                        className="flex items-center justify-between gap-3 w-full rounded-full bg-[#F8F8F8] px-4 py-2 text-gray-700 hover:bg-[#e9e9e9] transition-colors"
                        onClick={() => {
                            trackEvent('ai_history_new_chat_clicked', { model: chatModel === 'experiment' ? 'experiment' : 'insight' })
                            if (chatModel === 'experiment' && onNewExperimentChat) {
                                onNewExperimentChat()
                            } else {
                                setConversation(null)
                            }
                        }}
                    >
                        {/* Left section */}
                        <div className="flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                                <path
                                    d="M1.66668 4.16668C1.66668 4.38768 1.75448 4.59964 1.91076 4.75592C2.06704 4.9122 2.279 5 2.5 5C2.721 5 2.93296 4.9122 3.08924 4.75592C3.24552 4.59964 3.33332 4.38768 3.33332 4.16668V3.33332H4.16668C4.38768 3.33332 4.59964 3.24552 4.75592 3.08924C4.9122 2.93296 5 2.721 5 2.5C5 2.279 4.9122 2.06704 4.75592 1.91076C4.59964 1.75448 4.38768 1.66668 4.16668 1.66668H3.33332V0.833332C3.33332 0.61232 3.24552 0.400356 3.08924 0.244076C2.93296 0.087796 2.721 0 2.5 0C2.279 0 2.06704 0.087796 1.91076 0.244076C1.75448 0.400356 1.66668 0.61232 1.66668 0.833332V1.66668H0.833332C0.61232 1.66668 0.400356 1.75448 0.244076 1.91076C0.087796 2.06704 0 2.279 0 2.5C0 2.721 0.087796 2.93296 0.244076 3.08924C0.400356 3.24552 0.61232 3.33332 0.833332 3.33332H1.66668V4.16668ZM12.5 1.66668H7.5C7.279 1.66668 7.06704 1.75448 6.91076 1.91076C6.75448 2.06704 6.66668 2.279 6.66668 2.5C6.66668 2.721 6.75448 2.93296 6.91076 3.08924C7.06704 3.24552 7.279 3.33332 7.5 3.33332H12.5C12.721 3.33332 12.933 3.42112 13.0892 3.5774C13.2455 3.73368 13.3333 3.94564 13.3333 4.16668V12.2667L12.025 11.0583C11.8713 10.9144 11.6689 10.834 11.4583 10.8333H4.16668C3.94564 10.8333 3.73368 10.7455 3.5774 10.5892C3.42112 10.433 3.33332 10.221 3.33332 10V7.5C3.33332 7.279 3.24552 7.06704 3.08924 6.91076C2.93296 6.75448 2.721 6.66668 2.5 6.66668C2.279 6.66668 2.06704 6.75448 1.91076 6.91076C1.75448 7.06704 1.66668 7.279 1.66668 7.5V10C1.66668 10.663 1.93004 11.2989 2.39888 11.7678C2.86776 12.2366 3.50364 12.5 4.16668 12.5H11.1333L13.6333 14.775C13.7785 14.9113 13.9677 14.9911 14.1667 15C14.281 14.9988 14.394 14.9762 14.5 14.9333C14.6491 14.8683 14.7758 14.761 14.8647 14.6248C14.9535 14.4886 15.0006 14.3293 15 14.1667V4.16668C15 3.50364 14.7366 2.86776 14.2678 2.39888C13.7989 1.93004 13.163 1.66668 12.5 1.66668Z"
                                    fill="#161616"
                                />
                            </svg>
                            <span className="text-sm font-medium">{t('New Chat')}</span>
                        </div>

                        {/* Shortcut badge */}
                        <span className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs text-gray-500">{t('Ctrl + J')}</span>
                    </button>

                    {chatModel === 'experiment' ? (
                        <div className="flex flex-col gap-4 flex-1 overflow-auto">
                            {Object.entries(groupSessionsByDate(experimentSessions)).map(([section, sessions]) => (
                                <CollapsibleSection key={section} title={t(section)}>
                                    <div className="space-y-0.5">
                                        {sessions.map((session) => {
                                            const isActive = session.session_id === activeSessionId
                                            return (
                                                <button
                                                    type="button"
                                                    key={session.session_id}
                                                    className={`flex items-center justify-between w-full group px-4 py-2 cursor-pointer text-left hover:bg-gray-50 transition ${isActive ? 'bg-gray-100' : ''}`}
                                                    onClick={() => {
                                                        trackEvent('ai_history_experiment_session_selected', { session_id: session.session_id })
                                                        onLoadExperimentSession?.(session.session_id)
                                                    }}
                                                >
                                                    <div className="flex items-center gap-2 truncate">
                                                        <FiMessageSquare className="text-gray-400 flex-shrink-0" size={14} />
                                                        <span className="truncate text-xs font-medium text-gray-700">{getSessionLabel(session)}</span>
                                                    </div>
                                                    <span className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 flex-shrink-0">
                                                        <FiMoreHorizontal size={14} />
                                                    </span>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </CollapsibleSection>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4 flex-1 overflow-auto">
                            {Object.entries(grouped).map(([section, items]: any) => (
                                <CollapsibleSection key={section} title={t(section)}>
                                    <ul className="space-y-0.5">
                                        {items.map((chat: ChatItem) => (
                                            <ChatRow key={chat.conversation_id} chat={chat} setConversation={setConversation} />
                                        ))}
                                    </ul>
                                </CollapsibleSection>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
export default TestOpsGenieHistory
